import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useWindowManager } from "../../context/WindowManagerContext";

const STORAGE_KEY_ACCENT = "opentool-accent-color";
const STORAGE_KEY_BG_STYLE = "opentool-bg-style";
const STORAGE_KEY_WINDOW_OPACITY = "opentool-window-opacity";

const ACCENT_PRESETS = [
  { name: "Blue", color: "#4f8fff" },
  { name: "Purple", color: "#a78bfa" },
  { name: "Cyan", color: "#38bdf8" },
  { name: "Teal", color: "#7dcfff" },
  { name: "Green", color: "#34d399" },
  { name: "Orange", color: "#ff9f43" },
  { name: "Pink", color: "#ff79c6" },
];

type BgStyle = "space" | "minimal" | "nebula" | "chill" | "circuit-board";

const PRESET_DESCRIPTIONS: Record<string, string> = {
  focus: "Chat maximized",
  dashboard: "Home + Chat tiled",
  "command-center": "Three-pane split",
  monitor: "Home maximized",
  development: "Files + Chat + Browser",
  writer: "Notebook + Chat",
};

const PRESET_ICONS: Record<string, ReactNode> = {
  focus: (
    <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="30" height="22" rx="2" />
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="18" height="22" rx="2" />
      <rect x="20" y="1" width="11" height="22" rx="2" />
    </svg>
  ),
  "command-center": (
    <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="14" height="10" rx="2" />
      <rect x="1" y="13" width="14" height="10" rx="2" />
      <rect x="17" y="1" width="14" height="22" rx="2" />
    </svg>
  ),
  monitor: (
    <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="30" height="22" rx="2" />
      <line x1="6" y1="6" x2="26" y2="6" strokeOpacity="0.4" />
      <line x1="6" y1="10" x2="20" y2="10" strokeOpacity="0.4" />
    </svg>
  ),
  development: (
    <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="18" height="22" rx="2" />
      <rect x="20" y="1" width="11" height="10" rx="2" />
      <rect x="20" y="13" width="11" height="10" rx="2" />
    </svg>
  ),
  writer: (
    <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="17" height="22" rx="2" />
      <rect x="19" y="1" width="12" height="22" rx="2" />
    </svg>
  ),
};

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = hex.replace("#", "").match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!match) return null;
  return { r: parseInt(match[1], 16), g: parseInt(match[2], 16), b: parseInt(match[3], 16) };
}

function applyAccentColor(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  const doc = document.documentElement.style;
  doc.setProperty("--ot-accent", hex);
  doc.setProperty("--ot-accent-dim", `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`);
  doc.setProperty("--ot-accent-glow", `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`);
  doc.setProperty("--ot-border-hover", `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`);
  doc.setProperty("--ot-window-border-active", `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.55)`);
  doc.setProperty("--ot-taskbar-border", `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`);
  doc.setProperty("--ot-shadow-glow", `0 0 20px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`);
  doc.setProperty("--ot-window-shadow-active", `0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.18), 0 0 30px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`);
  localStorage.setItem(STORAGE_KEY_ACCENT, hex);
}

function applyBgStyle(style: BgStyle) {
  const desktop = document.querySelector(".wm-desktop");
  if (!desktop) return;
  desktop.classList.remove("wm-desktop--space", "wm-desktop--minimal", "wm-desktop--nebula", "wm-desktop--chill", "wm-desktop--circuit-board");
  desktop.classList.add(`wm-desktop--${style}`);
  localStorage.setItem(STORAGE_KEY_BG_STYLE, style);
}

function applyWindowOpacity(opacity: number) {
  const rgb = "30, 30, 54";
  document.documentElement.style.setProperty("--ot-window-bg", `rgba(${rgb}, ${opacity})`);
  localStorage.setItem(STORAGE_KEY_WINDOW_OPACITY, String(opacity));
}

// Initialize persisted personalization on load
export function initPersonalization() {
  const accent = localStorage.getItem(STORAGE_KEY_ACCENT);
  if (accent) applyAccentColor(accent);

  const bg = localStorage.getItem(STORAGE_KEY_BG_STYLE) as BgStyle | null;
  if (bg) {
    // Defer to ensure DOM is ready
    requestAnimationFrame(() => applyBgStyle(bg));
  }

  const opacity = localStorage.getItem(STORAGE_KEY_WINDOW_OPACITY);
  if (opacity) applyWindowOpacity(parseFloat(opacity));
}

interface PersonalizationMenuProps {
  onClose: () => void;
}

type Section = "theme" | "layout" | "ai";

export function PersonalizationMenu({ onClose }: PersonalizationMenuProps) {
  const { applyPreset, saveCurrentLayout, loadSavedLayouts, deleteSavedLayout, getBuiltInPresets } = useWindowManager();
  const [section, setSection] = useState<Section>("theme");
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem(STORAGE_KEY_ACCENT) || "#4f8fff");
  const [customHex, setCustomHex] = useState("");
  const [bgStyle, setBgStyle] = useState<BgStyle>(() => (localStorage.getItem(STORAGE_KEY_BG_STYLE) as BgStyle) || "space");
  const [windowOpacity, setWindowOpacity] = useState(() => parseFloat(localStorage.getItem(STORAGE_KEY_WINDOW_OPACITY) || "0.96"));
  const [savedLayouts, setSavedLayouts] = useState(() => loadSavedLayouts());
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  const builtInPresets = getBuiltInPresets();

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // Check if click is on the palette button itself
        const target = e.target as HTMLElement;
        if (target.closest(".wm-taskbar__tray-palette")) return;
        onClose();
      }
    };
    // Delay adding listener to avoid immediate close
    const timeout = setTimeout(() => {
      document.addEventListener("pointerdown", onClick);
    }, 100);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("pointerdown", onClick);
    };
  }, [onClose]);

  const handleAccentChange = useCallback((color: string) => {
    setAccentColor(color);
    applyAccentColor(color);
  }, []);

  const handleCustomHex = useCallback(() => {
    const hex = customHex.startsWith("#") ? customHex : `#${customHex}`;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      handleAccentChange(hex);
      setCustomHex("");
    }
  }, [customHex, handleAccentChange]);

  const handleBgChange = useCallback((style: BgStyle) => {
    setBgStyle(style);
    applyBgStyle(style);
  }, []);

  const handleOpacityChange = useCallback((val: number) => {
    setWindowOpacity(val);
    applyWindowOpacity(val);
  }, []);

  const handleSaveLayout = useCallback(() => {
    if (!saveName.trim()) return;
    saveCurrentLayout(saveName.trim());
    setSavedLayouts(loadSavedLayouts());
    setSaveName("");
    setShowSaveInput(false);
  }, [saveName, saveCurrentLayout, loadSavedLayouts]);

  const handleDeleteLayout = useCallback((id: string) => {
    deleteSavedLayout(id);
    setSavedLayouts(loadSavedLayouts());
  }, [deleteSavedLayout, loadSavedLayouts]);

  const handleApplyPreset = useCallback((id: string) => {
    applyPreset(id);
  }, [applyPreset]);

  const QUICK_THEMES = [
    { id: "neon", name: "Neon", desc: "Space + blue glow", accent: "#4f8fff", bg: "space" as BgStyle, opacity: 0.96, layout: null },
    { id: "chill", name: "Chill", desc: "Calm + teal focus", accent: "#7dcfff", bg: "chill" as BgStyle, opacity: 0.95, layout: "writer" },
  ];

  const handleApplyQuickTheme = useCallback((theme: typeof QUICK_THEMES[number]) => {
    handleAccentChange(theme.accent);
    handleBgChange(theme.bg);
    handleOpacityChange(theme.opacity);
    if (theme.layout) {
      handleApplyPreset(theme.layout);
    }
  }, [handleAccentChange, handleBgChange, handleOpacityChange, handleApplyPreset]);

  return (
    <div className="pm-panel" ref={panelRef}>
      {/* Header */}
      <div className="pm-header">
        <h3 className="pm-header__title">Personalize</h3>
        <button className="pm-header__close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Section tabs */}
      <div className="pm-tabs">
        {([
          { id: "theme" as const, label: "Theme" },
          { id: "layout" as const, label: "Layout" },
          { id: "ai" as const, label: "AI Theme" },
        ]).map(tab => (
          <button
            key={tab.id}
            className={`pm-tabs__btn ${section === tab.id ? "pm-tabs__btn--active" : ""}`}
            onClick={() => setSection(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="pm-content">
        {/* ── Theme Section ── */}
        {section === "theme" && (
          <div className="pm-section">
            {/* Quick Themes */}
            <div className="pm-group">
              <label className="pm-label">Quick Themes</label>
              <div className="pm-quick-themes">
                {QUICK_THEMES.map(theme => (
                  <button
                    key={theme.id}
                    className={`pm-theme-card ${accentColor === theme.accent && bgStyle === theme.bg ? "pm-theme-card--active" : ""}`}
                    onClick={() => handleApplyQuickTheme(theme)}
                  >
                    <div className={`pm-theme-card__preview pm-bg-preview--${theme.bg}`} />
                    <span className="pm-theme-card__name">{theme.name}</span>
                    <span className="pm-theme-card__desc">{theme.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Accent Color */}
            <div className="pm-group">
              <label className="pm-label">Accent Color</label>
              <div className="pm-swatches">
                {ACCENT_PRESETS.map(preset => (
                  <button
                    key={preset.color}
                    className={`pm-swatch ${accentColor === preset.color ? "pm-swatch--active" : ""}`}
                    style={{ background: preset.color }}
                    title={preset.name}
                    onClick={() => handleAccentChange(preset.color)}
                  />
                ))}
              </div>
              <div className="pm-custom-hex">
                <input
                  type="text"
                  className="pm-input"
                  placeholder="#hex..."
                  value={customHex}
                  onChange={(e) => setCustomHex(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCustomHex()}
                  maxLength={7}
                />
                <button className="pm-btn-sm" onClick={handleCustomHex}>Apply</button>
              </div>
            </div>

            {/* Background Style */}
            <div className="pm-group">
              <label className="pm-label">Background</label>
              <div className="pm-bg-options">
                {([
                  { id: "space" as BgStyle, label: "Space", desc: "Starfield with nebula" },
                  { id: "minimal" as BgStyle, label: "Minimal", desc: "Solid dark background" },
                  { id: "nebula" as BgStyle, label: "Nebula", desc: "Vibrant gradients" },
                  { id: "chill" as BgStyle, label: "Chill", desc: "Calm gradient, no particles" },
                  { id: "circuit-board" as BgStyle, label: "Circuit", desc: "PCB trace pattern" },
                ]).map(opt => (
                  <button
                    key={opt.id}
                    className={`pm-bg-option ${bgStyle === opt.id ? "pm-bg-option--active" : ""}`}
                    onClick={() => handleBgChange(opt.id)}
                  >
                    <div className={`pm-bg-preview pm-bg-preview--${opt.id}`} />
                    <div className="pm-bg-option__text">
                      <span className="pm-bg-option__name">{opt.label}</span>
                      <span className="pm-bg-option__desc">{opt.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Window Opacity */}
            <div className="pm-group">
              <label className="pm-label">
                Window Opacity
                <span className="pm-label__value">{Math.round(windowOpacity * 100)}%</span>
              </label>
              <input
                type="range"
                className="pm-slider"
                min="0.8"
                max="1.0"
                step="0.02"
                value={windowOpacity}
                onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
              />
            </div>
          </div>
        )}

        {/* ── Layout Section ── */}
        {section === "layout" && (
          <div className="pm-section">
            <div className="pm-group">
              <label className="pm-label">Presets</label>
              <div className="pm-presets">
                {builtInPresets.map(preset => (
                  <button
                    key={preset.id}
                    className="pm-preset-card"
                    onClick={() => handleApplyPreset(preset.id)}
                  >
                    <div className="pm-preset-card__icon">
                      {PRESET_ICONS[preset.id]}
                    </div>
                    <div className="pm-preset-card__info">
                      <span className="pm-preset-card__name">{preset.name}</span>
                      <span className="pm-preset-card__desc">{PRESET_DESCRIPTIONS[preset.id]}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="pm-group">
              <label className="pm-label">Save Current Layout</label>
              {showSaveInput ? (
                <div className="pm-save-input">
                  <input
                    type="text"
                    className="pm-input"
                    placeholder="Layout name..."
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSaveLayout()}
                    autoFocus
                  />
                  <button className="pm-btn-sm pm-btn-sm--accent" onClick={handleSaveLayout}>Save</button>
                  <button className="pm-btn-sm" onClick={() => { setShowSaveInput(false); setSaveName(""); }}>Cancel</button>
                </div>
              ) : (
                <button className="pm-btn-save" onClick={() => setShowSaveInput(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Save current window arrangement
                </button>
              )}
            </div>

            {savedLayouts.length > 0 && (
              <div className="pm-group">
                <label className="pm-label">Saved Layouts</label>
                <div className="pm-saved-list">
                  {savedLayouts.map(layout => (
                    <div key={layout.id} className="pm-saved-item">
                      <span className="pm-saved-item__name">{layout.name}</span>
                      <span className="pm-saved-item__count">{layout.windows.length} windows</span>
                      <button className="pm-btn-sm pm-btn-sm--accent" onClick={() => handleApplyPreset(layout.id)}>Apply</button>
                      <button className="pm-btn-sm pm-btn-sm--danger" onClick={() => handleDeleteLayout(layout.id)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── AI Theme Section ── */}
        {section === "ai" && (
          <div className="pm-section">
            <div className="pm-group">
              <label className="pm-label">AI Theme Generator</label>
              <p className="pm-desc">Describe the vibe you want and let AI generate a custom theme for your workspace.</p>
              <textarea
                className="pm-textarea"
                placeholder="Describe your ideal theme..."
                rows={3}
                disabled
              />
              <div className="pm-ai-actions">
                <button className="pm-btn-generate" disabled title="Coming soon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                  Generate
                  <span className="pm-coming-soon">Coming soon</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
